import { memo, Dispatch, SetStateAction } from "react";

import { useFormContext, UseFormSetValue } from "react-hook-form";
import { Box, Button } from "@mui/material";
import ImageDialog from "@app/components/ImageDialog";
import RenderNewUploadingImagesList from "@app/components/RenderNewUploadingImagesList";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { useRenderFileViewHook } from "@app/hooks/imagesRender";
import { ApiEndpoint, imagesFileFormats } from "@constants/index";

import {
  CreateMarkState,
  ExistingImagesKeys,
  FileListKeys,
  IFlowProcess,
  OnChangeFunctionProps,
  IIngredientFormData
} from "@pages/IngredientsMutation/helper/types";
import { MainLayer, ContentWrapper, VisuallyHiddenInput } from "./styled";

interface ImagesContainerProps {
  keyUploadedFileList: FileListKeys;
  keyMainImageUrl: 'mainImageUrl';
  keyExistingImages: ExistingImagesKeys;
  buttonText: string;
  buttontype?: 'text' | 'outlined' | 'contained';
  isImageViewOpen: boolean;
  imageSrcForAction: string;
  multiple: boolean;
  posibilityToMainImage: boolean;
  setValue: UseFormSetValue<IIngredientFormData>;
  handleDeleteImage: (str: any) => void;
  setFlowProcess: Dispatch<SetStateAction<IFlowProcess>>;
  onChangeUpload: (props: OnChangeFunctionProps) => void;
}

const ImagesContainer = ({
  keyUploadedFileList,
  keyMainImageUrl,
  keyExistingImages,
  buttonText,
  buttontype = 'contained',
  imageSrcForAction,
  isImageViewOpen,
  multiple,
  posibilityToMainImage,
  setValue,
  setFlowProcess,
  onChangeUpload,
  handleDeleteImage,
}: ImagesContainerProps) => {
  const { watch, formState: { errors } } = useFormContext<IIngredientFormData>()
  const image = watch(keyUploadedFileList)
  const mainImageUrl = watch(keyMainImageUrl)
  const existingImages = watch(keyExistingImages)

  const isUploadedImage = image && 'url' in image

  const previews = useRenderFileViewHook(image, existingImages)

  return (
    <MainLayer>
      <ContentWrapper>
        <RenderNewUploadingImagesList<CreateMarkState>
          togetherImagesLength={isUploadedImage ? 1 : previews.length}
          newPreviewsList={isUploadedImage ? [`${ApiEndpoint}/${image.url}`] : previews}
          setState={setFlowProcess}
          handleDeleteImage={handleDeleteImage}
          posibilityToMainImage={posibilityToMainImage}
          mainImageUrl={mainImageUrl}
          setMainImage={(id: string) => setValue(keyMainImageUrl, id)}
        />
        <ImageDialog
          url={imageSrcForAction}
          isOpen={isImageViewOpen}
          onClose={() => {
            setFlowProcess((prev) => ({ ...prev, isImageViewOpen: false }))
          }}
        />
      </ContentWrapper>
      <Box color="red">
        {errors?.[keyUploadedFileList]?.message ? "Ingredient image is required" : ""}
      </Box>
      <Box
        sx={{ mt: 1, display: 'flex', gap: 1 }}
      >
        <Button
          component="label"
          role={undefined}
          variant={buttontype}
          tabIndex={-1}
          startIcon={<CloudUploadIcon />}
        >
          <p>{ image?.length ? 'Update Files' : buttonText }</p>
          <VisuallyHiddenInput
            type="file"
            id="files"
            multiple={multiple}
            name="images"
            accept={imagesFileFormats.join(',')}
            onChange={(event) => onChangeUpload({
              event,
              multiple,
              keyExistingImages,
              keyUploadedFileList,
            })}
          />
        </Button>
      </Box>
    </MainLayer>
  )
}

export default memo(ImagesContainer)
